import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LegacyStatus } from '@vendasta/notifications-sdk';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { PartnerService } from '../../core/partner.service';
import { User } from '../../core/user';
import { LegacyNotificationPreferenceService } from '../../user-notifications/legacy-notification-preference/legacy-notification-preference.service';
import { BusinessCenterTab } from '../../user-permissions/account-group-permissions/business-center-tab';
import { TabApiService } from '../../user-permissions/tab.api.service';
import { NotificationChange } from './notification-change';

export interface UserTabAccessInfo {
  selectedUsers: User[];
  allUsers: boolean;
  tabs: BusinessCenterTab[];
}

export interface TabChange {
  name: string;
  id: string;
}

@Injectable({ providedIn: 'root' })
export class UserBulkActionsService {
  private selectedUsers$$ = new BehaviorSubject<User[]>(null);
  public selectedUsers$ = this.selectedUsers$$.asObservable();
  private allUsers: boolean;
  private totalUserCount: number;
  private tabs: BusinessCenterTab[];

  private tabsGiveAccess$$: BehaviorSubject<TabChange[]> = new BehaviorSubject<TabChange[]>([]);
  readonly tabsGiveAccess$: Observable<TabChange[]> = this.tabsGiveAccess$$.asObservable();
  private tabsRemoveAccess$$: BehaviorSubject<TabChange[]> = new BehaviorSubject<TabChange[]>([]);
  readonly tabsRemoveAccess$: Observable<TabChange[]> = this.tabsRemoveAccess$$.asObservable();

  private notificationSettings$$: BehaviorSubject<NotificationChange[]> = new BehaviorSubject<NotificationChange[]>([]);
  readonly notificationSettings$: Observable<NotificationChange[]> = this.notificationSettings$$.asObservable();
  public bulkUpdateScope = false;

  constructor(
    private partnerService: PartnerService,
    private tabApiService: TabApiService,
    private notificationPreferenceService: LegacyNotificationPreferenceService,
    private translateService: TranslateService,
  ) {}

  areUsersSelected(): boolean {
    return !!this.selectedUsers$$.value;
  }

  resetSelections(): void {
    this.tabsGiveAccess$$.next([]);
    this.tabsRemoveAccess$$.next([]);
    this.notificationSettings$$.next([]);
  }

  getAmountOfUsersSelectedStringAsync(): Observable<string> {
    if (this.allUsers) {
      return this.translateService.stream('USERS.BULK_UPDATE_SETTINGS.NUM_USERS', {
        numUsers: this.totalUserCount,
      });
    }
    const userNumber = this.selectedUsers$$.value.length;
    const text$ = this.translateService.stream('USERS.BULK_UPDATE_SETTINGS.NUM_USERS', {
      numUsers: userNumber,
    });
    if (userNumber === 1) {
      return combineLatest([this.selectedUsers$, text$]).pipe(
        map(([users, text]) => {
          return users[0]?.fullName ? users[0].fullName : text;
        }),
      );
    }
    return text$;
  }

  getUserTabAccessInfo(): UserTabAccessInfo {
    return {
      selectedUsers: this.selectedUsers$$.value,
      allUsers: this.allUsers,
      tabs: this.tabs,
    };
  }

  setSelectedUsersInfo(selectedUsers: User[], allUsers: boolean, totalUserCount: number): void {
    this.selectedUsers$$.next(selectedUsers);
    this.allUsers = allUsers;
    this.totalUserCount = totalUserCount;
  }

  setAvailableTabs(tabs: BusinessCenterTab[]): void {
    this.tabs = tabs;
  }

  setTabAccess(tabsGiveAccess: TabChange[], tabsRemoveAccess: TabChange[]): void {
    this.tabsGiveAccess$$.next(tabsGiveAccess);
    this.tabsRemoveAccess$$.next(tabsRemoveAccess);
  }

  setNotificationUpdate(update: NotificationChange): void {
    let changes = this.notificationSettings$$.value;
    const index = changes.findIndex((existing) => {
      return existing.section === update.section && existing.setting === update.setting;
    });
    if (index === -1) {
      changes.push(update);
    } else {
      changes[index] = update;
    }
    changes = changes.filter((change) => change.valueFlag !== LegacyStatus.STATUS_UNSET);
    this.notificationSettings$$.next(changes);
  }

  setBulkUpdateScope(val: boolean): void {
    this.bulkUpdateScope = val;
  }

  submitChanges(): Observable<any> {
    return combineLatest([
      this.requestTabAccessChanges(
        this.selectedUsers$$.value.map((user) => user.userId),
        this.tabs.map((tab) => tab.id),
      ),
      this.requestNotificationChanges(),
    ]);
  }

  private requestTabAccessChanges(userIds: string[], tabIds: string[]): Observable<void> {
    return combineLatest([this.tabsGiveAccess$, this.tabsRemoveAccess$]).pipe(
      tap(([tabsGivenAccess, tabsRemovedAccess]) => {
        if (tabsGivenAccess.length === 0 && tabsRemovedAccess.length === 0) {
          throw new Error('No Tabs to update');
        }
      }),
      switchMap(([tabsGivenAccess, tabsRemovedAccess]) => {
        return this.tabApiService.setMulti(
          userIds,
          tabIds,
          tabsGivenAccess.map((tab) => tab.id),
          tabsRemovedAccess.map((tab) => tab.id),
        );
      }),
      catchError((err) => {
        return of(err.message);
      }),
    );
  }

  private requestNotificationChanges(): Observable<any> {
    return this.notificationSettings$.pipe(
      tap((permissions) => {
        if (permissions.length === 0) {
          throw new Error('No Permissions to update');
        }
      }),
      switchMap((permissions) => {
        return this.notificationPreferenceService.bulkUpdatePermissions(
          this.partnerService.partnerId,
          this.selectedUsers$$.value.map((user) => user.unifiedUserId),
          permissions,
          !this.bulkUpdateScope,
        );
      }),
      tap(() => (this.bulkUpdateScope = false)), //reset to default
      catchError((err) => {
        return of(err.message);
      }),
    );
  }
}
